import React from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { FormField } from '../../FormField/FormField';
import { AdaptiveSelect } from '../../AdaptiveSelect/AdaptiveSelect';
import { LOCALES } from '../../../config/locales';
import { ValidationErrors } from '../../../hooks/useForm';
import { RoutineExercise } from '../../../types/Fisiofit/routine';
import { EXERCISE_TYPE, ExerciseShort } from '../../../types/Fisiofit/exercise';
import { MUSCLE_GROUPS, EXERCISE_CATEGORIES } from '../../../types/Fisiofit/fisiofitConst';
import { getExerciseOptions } from '../../../utils/selectOptions';
import { RoutineForm } from '../validators';
import { RoutineSupersetsActions } from '../types';

interface SupersetExerciseFieldsProps {
  supersetExercise: RoutineExercise;
  dayIndex: number;
  exerciseIndex: number;
  supersetExIndex: number;
  validationErrors: ValidationErrors<RoutineForm> | undefined;
  locale: LOCALES;
  organizationExercises: ExerciseShort[] | undefined;
  selectedExerciseMuscleGroups: MUSCLE_GROUPS[];
  selectedExerciseCategories: EXERCISE_CATEGORIES[];
  supersets: RoutineSupersetsActions;
}

export const SupersetExerciseFields = ({
  supersetExercise,
  dayIndex,
  exerciseIndex,
  supersetExIndex,
  validationErrors,
  locale,
  organizationExercises,
  selectedExerciseMuscleGroups,
  selectedExerciseCategories,
  supersets,
}: SupersetExerciseFieldsProps) => {
  return (
    <div className='exercise-form-fields'>
      <FormField
        labelTitle='Ejercicio'
        smallLabel
        fullWidth
        error={!!validationErrors?.exerciseDays && !supersetExercise.exerciseId ? 'Debes elegir un ejercicio' : undefined}
        dontShowErrorMessage
      >
        <AdaptiveSelect
          value={supersetExercise.exerciseId}
          options={
            organizationExercises
              ? getExerciseOptions(organizationExercises, selectedExerciseMuscleGroups, selectedExerciseCategories)
              : []
          }
          onChange={(value: string) => {
            supersets.handleSupersetExerciseSelect(dayIndex, exerciseIndex, supersetExIndex, value);
          }}
          placeholder='Selecciona un ejercicio'
          modalTitle='Seleccionar ejercicio'
          searchPlaceholder='Buscar ejercicio'
          emptyMessage='No hay ejercicios'
          emptyFilterMessage='No se han encontrado ejercicios'
          itemTemplate={(option) => <span>{option.label}</span>}
          invalid={!!validationErrors?.exerciseDays && !supersetExercise.exerciseId}
        />
      </FormField>

      {supersetExercise.exerciseId && (
        <>
          <FormField className='exercise-field' labelTitle='Series' smallLabel>
            <InputNumber
              value={supersetExercise.sets}
              onChange={(e) => {
                e.originalEvent?.stopPropagation();
                supersets.updateSupersetExercise(dayIndex, exerciseIndex, supersetExIndex, {
                  sets: e.value ?? undefined,
                });
              }}
              min={1}
              max={100}
              useGrouping={false}
              locale={locale === 'es' ? 'es-ES' : 'en-US'}
              minFractionDigits={0}
              maxFractionDigits={0}
              onClick={(e) => e.stopPropagation()}
            />
          </FormField>

          {supersetExercise.exerciseType === EXERCISE_TYPE.REPS ? (
            <FormField className='exercise-field' labelTitle='Reps' smallLabel>
              <InputNumber
                value={'repetitions' in supersetExercise ? supersetExercise.repetitions : undefined}
                onChange={(e) => {
                  e.originalEvent?.stopPropagation();
                  supersets.updateSupersetExercise(dayIndex, exerciseIndex, supersetExIndex, {
                    repetitions: e.value ?? undefined,
                  });
                }}
                min={1}
                max={1000}
                useGrouping={false}
                locale={locale === 'es' ? 'es-ES' : 'en-US'}
                minFractionDigits={0}
                maxFractionDigits={0}
                onClick={(e) => e.stopPropagation()}
              />
            </FormField>
          ) : (
            <FormField className='exercise-field' labelTitle='Tiempo (s)' smallLabel>
              <InputNumber
                value={'duration' in supersetExercise ? supersetExercise.duration : undefined}
                onChange={(e) => {
                  e.originalEvent?.stopPropagation();
                  supersets.updateSupersetExercise(dayIndex, exerciseIndex, supersetExIndex, {
                    duration: e.value ?? undefined,
                  });
                }}
                min={1}
                max={3600}
                useGrouping={false}
                locale={locale === 'es' ? 'es-ES' : 'en-US'}
                minFractionDigits={0}
                maxFractionDigits={0}
                onClick={(e) => e.stopPropagation()}
              />
            </FormField>
          )}

          <FormField className='exercise-field' labelTitle='Peso (kg)' smallLabel>
            <InputNumber
              value={supersetExercise.weight ?? null}
              onChange={(e) => {
                e.originalEvent?.stopPropagation();
                supersets.updateSupersetExercise(dayIndex, exerciseIndex, supersetExIndex, {
                  weight: e.value ?? undefined,
                });
              }}
              min={0}
              max={999}
              useGrouping={false}
              locale={locale === 'es' ? 'es-ES' : 'en-US'}
              minFractionDigits={0}
              maxFractionDigits={2}
              onClick={(e) => e.stopPropagation()}
            />
          </FormField>

          <FormField className='exercise-field' labelTitle='RIR' smallLabel>
            <InputNumber
              value={supersetExercise.rir}
              onChange={(e) => {
                e.originalEvent?.stopPropagation();
                supersets.updateSupersetExercise(dayIndex, exerciseIndex, supersetExIndex, {
                  rir: e.value ?? undefined,
                });
              }}
              min={0}
              max={10}
              useGrouping={false}
              locale={locale === 'es' ? 'es-ES' : 'en-US'}
              minFractionDigits={0}
              maxFractionDigits={0}
              onClick={(e) => e.stopPropagation()}
            />
          </FormField>

          <FormField className='exercise-field' labelTitle='RPE' smallLabel>
            <InputNumber
              value={supersetExercise.rpe}
              onChange={(e) => {
                e.originalEvent?.stopPropagation();
                supersets.updateSupersetExercise(dayIndex, exerciseIndex, supersetExIndex, {
                  rpe: e.value ?? undefined,
                });
              }}
              min={1}
              max={10}
              useGrouping={false}
              locale={locale === 'es' ? 'es-ES' : 'en-US'}
              minFractionDigits={0}
              maxFractionDigits={0}
              onClick={(e) => e.stopPropagation()}
            />
          </FormField>

          <FormField className='exercise-field comment-field' labelTitle='Comentario' smallLabel fullWidth>
            <InputTextarea
              value={supersetExercise.comment ?? ''}
              onChange={(e) => {
                e.stopPropagation();
                supersets.updateSupersetExercise(dayIndex, exerciseIndex, supersetExIndex, {
                  comment: e.target.value || undefined,
                });
              }}
              placeholder='Comentario'
              rows={3}
              onClick={(e) => e.stopPropagation()}
            />
          </FormField>
        </>
      )}
    </div>
  );
};
